// https://git.huya.info/huyafed/components/huyafed-npm/-/tree/master/packages/taf-network
import TafNetwork from "@huyafed/taf-network/dist/taf-network.cjs.prod.js";
import {
  createTafRuntime,
  type TafRequest,
  type TafTransport,
} from "@huyafed/gametool-taf-runtime";
import Constants from "@/constants";
import { withMiddleware } from "./middleware";
import * as kiwiSdk from "@huyafed/huya-kiwi";

type TBase = {
  /** @see https://git.huya.info/huyafed/components/huyafed-npm/-/tree/master/packages/taf-network#bcode-%E5%AF%B9%E5%BA%94%E7%9A%84%E7%8A%B6%E6%80%81 */
  bcode: number;

  /** @see https://git.huya.info/huyafed/components/huyafed-npm/-/tree/master/packages/taf-network#sdkcode-%E5%AF%B9%E5%BA%94%E7%9A%84%E7%8A%B6%E6%80%81 */
  sdkcode: number;

  [key: string]: any;
};
type TafClassIgnoreKey = "_clone" | "_write" | "_read" | "writeTo" | "readFrom";
export type ClassType = new (...args: any) => any;

type GT<T> = T extends ClassType ? T : any;
export type TReqParams<TReqClass extends any | ClassType> = TReqClass extends ClassType
  ? Partial<Omit<InstanceType<TReqClass>, TafClassIgnoreKey>>
  : Partial<Omit<TReqClass, TafClassIgnoreKey>>;
export type TRspInfer<T extends any> = T extends ClassType ? InstanceType<T> : T;
export type TRspWrap<T> = T & TBase;
export type TTafFetch = <TRspClass extends any = any, TReqClass extends any = any>(params: {
  /** 服务端的ui, 如 accompanyui */
  moduleName: string;
  /** 接口名, 如 reactiveQuickOrderInvitation */
  funcName: string;
  ReqClass: GT<TReqClass>;
  RspClass: GT<TRspClass>;
  data: TReqParams<TReqClass>;
  /** 超时时间，默认 6 秒 */
  timeoutMS?: number;
  /** 是否使用固定ua */
  useSendWupWithForceUa?: boolean;
}) => Promise<TRspWrap<TRspInfer<TRspClass>>>;

/**
 * 必须返回 HUYA.UserId 结构体实例。
 * jce 序列化 tId 字段走 JceOutputStream.writeStruct，内部会调用 value.writeTo；
 * 普通对象字面量没有该方法，会抛 "not defined writeTo Function" 导致请求本地失败。
 */
function createUserIdentity() {
  return kiwiSdk.PlatformCompat.getUserId();
}

/** 接口超时时间，单位秒 */
export const timeOutSeconds = 10;

export const tafSdk = new TafNetwork({
  showLog: Constants.IS_DEBUG || Constants.IS_USEDEBUG,
  // 工具 o85p2vmi 只在生产 TAF 注册，测试环境返回 501「工具不存在」，固定走生产
  isDebug: false,
  uris: {},
  tryTime: timeOutSeconds,
  kiwiSdk,
});

const runtimeTafFetch = createTafRuntime({
  transport: tafSdk as unknown as TafTransport,
  timeoutMS: timeOutSeconds * 1000,
});

/**
 * 增强版 tafFetch，支持中间件
 */
export const tafFetch = Object.assign(
  withMiddleware(
    ((params: Parameters<TTafFetch>[0]) =>
      runtimeTafFetch(params as unknown as TafRequest)) as TTafFetch,
  ),
  {
    getTaf: () => runtimeTafFetch.getTaf(),
  },
);

export default tafFetch;

export const getErrorMsgFromSDK = (
  res: Awaited<ReturnType<TTafFetch>>,
  prefixErrorMsg?: string,
) => {
  const shouldShowCode = Constants.IS_DEBUG || Constants.IS_USEDEBUG;
  const codeStr = shouldShowCode ? `【${res?.bcode}】-【${res?.sdkcode}】` : "";
  if (res?.bcode !== 0 || res?.sdkcode !== 0)
    return `${prefixErrorMsg || "网络繁忙，请稍后再试"}${codeStr}`;
  return "";
};

export const checkRes = (
  res: { bcode: number; sdkcode: number; [key: string]: any },
  prefixErrorMsg?: string,
) => {
  const errorMsg = getErrorMsgFromSDK(res, prefixErrorMsg);
  if (errorMsg) throw new Error(errorMsg);
};

// Re-export for middleware compatibility
export const getUserId = createUserIdentity;
